import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Pencil, Download, Printer, Plus, CreditCard,
  CheckCircle, Clock, AlertTriangle, Calendar, Building, User,
  Loader2, Trash2, RefreshCw, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { facturasAPI, contactosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  parcial: { label: 'Pago Parcial', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  pagada: { label: 'Pagada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  vencida: { label: 'Vencida', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  anulada: { label: 'Anulada', color: 'bg-gray-100 text-gray-800', icon: X },
};

const TIPOS = {
  proforma: { label: 'Proforma', color: 'text-blue-600' },
  factura: { label: 'Factura', color: 'text-green-600' },
  presupuesto: { label: 'Presupuesto', color: 'text-purple-600' },
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FacturaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPagoDialogOpen, setIsPagoDialogOpen] = useState(false);
  
  // Datos para edición
  const [contactos, setContactos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  
  // Form de pago
  const [pagoForm, setPagoForm] = useState({
    importe: '',
    metodo_pago: 'TRANSFERENCIA BANCARIA',
    referencia: '',
    descripcion: ''
  });
  
  // Form de edición
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    loadFactura();
    loadRelatedData();
  }, [id]);

  const loadFactura = async () => {
    try {
      setLoading(true);
      const res = await facturasAPI.getById(id);
      setFactura(res.data);
    } catch (error) {
      console.error('Error cargando factura:', error);
      toast({ title: "Error", description: "No se pudo cargar la factura", variant: "destructive" });
      navigate('/contabilidad');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedData = async () => {
    try {
      const [contactosRes, expedientesRes] = await Promise.all([
        contactosAPI.getAll(),
        expedientesAPI.getAll()
      ]);
      setContactos(contactosRes.data.filter(c => c.tipo === 'cliente'));
      setExpedientes(expedientesRes.data);
    } catch (error) {
      console.error('Error cargando datos relacionados:', error);
    }
  };

  const handleConvertirAFactura = async () => {
    if (!window.confirm('¿Convertir este documento a factura definitiva?')) return;
    
    try {
      setSaving(true);
      await facturasAPI.convertirAFactura(id);
      toast({ title: "Convertido a factura", description: "El documento se ha convertido a factura definitiva" });
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo convertir", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDescargarPdf = async () => {
    try {
      setSaving(true);
      const response = await facturasAPI.descargarPdf(id);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${factura.numero_factura.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: "PDF descargado" });
    } catch (error) {
      toast({ title: "Error", description: "No se pudo generar el PDF", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleRegistrarPago = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await facturasAPI.registrarPago(id, {
        importe: parseFloat(pagoForm.importe),
        metodo_pago: pagoForm.metodo_pago,
        referencia: pagoForm.referencia,
        descripcion: pagoForm.descripcion
      });
      
      toast({ title: "Pago registrado" });
      setIsPagoDialogOpen(false);
      setPagoForm({ importe: '', metodo_pago: 'TRANSFERENCIA BANCARIA', referencia: '', descripcion: '' });
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo registrar el pago", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarPago = async (pagoId) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    
    try {
      await facturasAPI.eliminarPago(id, pagoId);
      toast({ title: "Pago eliminado" });
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el pago", variant: "destructive" });
    }
  };

  const handleOpenEditDialog = () => {
    setEditForm({
      tipo: factura.tipo,
      cliente_id: factura.cliente_id?.toString() || '',
      expediente_id: factura.expediente_id?.toString() || '',
      serie: factura.serie || '',
      codigo_expediente: factura.codigo_expediente || '',
      descripcion: factura.descripcion || '',
      forma_pago: factura.forma_pago || 'TRANSFERENCIA BANCARIA',
      fecha_emision: factura.fecha_emision?.split('T')[0] || '',
      fecha_vencimiento: factura.fecha_vencimiento?.split('T')[0] || '',
      porcentaje_iva: factura.porcentaje_iva || 21,
      porcentaje_retencion: factura.porcentaje_retencion || 0,
      notas: factura.notas || '',
      conceptos: factura.conceptos || [],
      suplidos: factura.suplidos || []
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateFactura = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await facturasAPI.update(id, {
        ...editForm,
        cliente_id: editForm.cliente_id ? parseInt(editForm.cliente_id) : null,
        expediente_id: editForm.expediente_id ? parseInt(editForm.expediente_id) : null,
        porcentaje_iva: parseFloat(editForm.porcentaje_iva),
        porcentaje_retencion: parseFloat(editForm.porcentaje_retencion)
      });
      
      toast({ title: "Factura actualizada" });
      setIsEditDialogOpen(false);
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo actualizar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const addConcepto = () => {
    setEditForm({
      ...editForm,
      conceptos: [...editForm.conceptos, {
        concepto: '',
        descripcion: '',
        unidades: 1,
        precio: 0,
        impuesto_pct: editForm.porcentaje_iva,
        retencion_pct: editForm.porcentaje_retencion,
        base_imponible: 0
      }]
    });
  };

  const updateConcepto = (index, field, value) => {
    const newConceptos = [...editForm.conceptos];
    newConceptos[index] = { ...newConceptos[index], [field]: value };
    
    // Recalcular base_imponible
    if (field === 'unidades' || field === 'precio') {
      const unidades = field === 'unidades' ? parseFloat(value) : parseFloat(newConceptos[index].unidades);
      const precio = field === 'precio' ? parseFloat(value) : parseFloat(newConceptos[index].precio);
      newConceptos[index].base_imponible = unidades * precio;
    }
    
    setEditForm({ ...editForm, conceptos: newConceptos });
  };

  const removeConcepto = (index) => {
    const newConceptos = editForm.conceptos.filter((_, i) => i !== index);
    setEditForm({ ...editForm, conceptos: newConceptos });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!factura) return null;

  const estadoInfo = ESTADOS[factura.estado] || ESTADOS.pendiente;
  const tipoInfo = TIPOS[factura.tipo] || TIPOS.proforma;
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header con acciones */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/contabilidad')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <FileText className={`w-6 h-6 ${tipoInfo.color}`} />
              <h1 className="text-2xl font-bold">{tipoInfo.label} | {factura.numero_factura}</h1>
            </div>
            <Badge className={`mt-1 ${estadoInfo.color}`}>
              <EstadoIcon className="w-3 h-3 mr-1" />
              {estadoInfo.label}
            </Badge>
          </div>
        </div>
        
        <div className="flex gap-2">
          {factura.tipo !== 'factura' && (
            <Button 
              onClick={handleConvertirAFactura} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              CONVERTIR A FACTURA
            </Button>
          )}
          <Button variant="outline" onClick={handleOpenEditDialog}>
            <Pencil className="w-4 h-4 mr-2" />
            EDITAR
          </Button>
          <Button variant="outline" onClick={handleDescargarPdf} disabled={saving}>
            <Download className="w-4 h-4 mr-2" />
            DESCARGAR/IMPRIMIR
          </Button>
          {factura.estado !== 'pagada' && factura.estado !== 'anulada' && (
            <Button 
              onClick={() => {
                setPagoForm({ ...pagoForm, importe: factura.total_pendiente.toString() });
                setIsPagoDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              AÑADIR COBRO
            </Button>
          )}
        </div>
      </div>

      {/* Vista previa tipo factura */}
      <Card className="border-2 shadow-lg">
        <CardContent className="p-8">
          {/* Cabecera del documento */}
          <div className="flex justify-between mb-8 pb-6 border-b">
            <div>
              <h2 className="text-3xl font-bold text-blue-700 mb-2">lex duo</h2>
              <p className="text-sm text-gray-600">EDUARD CAULA PARETAS</p>
              <p className="text-sm text-gray-500">NIF / CIF: 40332926B</p>
              <p className="text-sm text-gray-500">avda. Jaume I, 82 pis 3 porta 2 17002</p>
              <p className="text-sm text-gray-500">Girona, Cataluña, España</p>
              <p className="text-sm text-gray-500 mt-2">644 347 002</p>
              <p className="text-sm text-blue-600">info@lexduo.cat</p>
            </div>
            <div className="text-right">
              <p className="text-sm"><strong>{tipoInfo.label}:</strong> {factura.numero_factura}</p>
              <p className="text-sm"><strong>Serie:</strong> {factura.serie || 'EDUARD CAULA PARETAS'}</p>
              <p className="text-sm"><strong>Fecha:</strong> {formatDate(factura.fecha_emision)}</p>
              {factura.codigo_expediente && (
                <p className="text-sm"><strong>Cód. Exp:</strong> {factura.codigo_expediente}</p>
              )}
              <p className="text-sm"><strong>Forma de pago:</strong> {factura.forma_pago}</p>
            </div>
          </div>

          {/* Cliente */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">CLIENTE</p>
            <p className="font-bold text-lg">{factura.cliente_nombre}</p>
            {factura.cliente_nif && <p className="text-sm">CIF/NIF: {factura.cliente_nif}</p>}
            {factura.cliente_direccion && <p className="text-sm text-gray-600">{factura.cliente_direccion}</p>}
          </div>

          {/* Descripción */}
          {factura.descripcion && (
            <div className="mb-6">
              <p className="text-sm"><strong>Descripción:</strong> {factura.descripcion}</p>
            </div>
          )}

          {/* Conceptos facturables */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-blue-600 mb-4">Conceptos facturables</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Concepto</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Unidades</TableHead>
                  <TableHead className="text-right">Precio</TableHead>
                  <TableHead className="text-right">Impuesto(%)</TableHead>
                  <TableHead className="text-right">Retención(%)</TableHead>
                  <TableHead className="text-right">Base Imponible</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(factura.conceptos || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500">No hay conceptos</TableCell>
                  </TableRow>
                ) : (
                  factura.conceptos.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{c.concepto}</TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-xs">{c.descripcion}</TableCell>
                      <TableCell className="text-right">{c.unidades?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(c.precio)}</TableCell>
                      <TableCell className="text-right">{c.impuesto_pct || 21}%</TableCell>
                      <TableCell className="text-right">{c.retencion_pct || 0}%</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(c.base_imponible || (c.unidades * c.precio))}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Suplidos */}
          {(factura.suplidos || []).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-blue-600 mb-4">Suplidos</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Concepto</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="text-right">Unidades</TableHead>
                    <TableHead className="text-right">Precio</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {factura.suplidos.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{s.concepto}</TableCell>
                      <TableCell className="text-sm text-gray-600">{s.descripcion}</TableCell>
                      <TableCell className="text-right">{s.unidades?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(s.precio)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(s.total || (s.unidades * s.precio))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Totales */}
          <div className="flex justify-end">
            <div className="w-80">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Base Imponible</span>
                  <span>{formatCurrency(factura.base_imponible)}</span>
                </div>
                <div className="flex justify-between">
                  <span>% IVA/IGIC ({factura.porcentaje_iva}%)</span>
                  <span>{formatCurrency(factura.importe_iva)}</span>
                </div>
                {factura.porcentaje_retencion > 0 && (
                  <div className="flex justify-between">
                    <span>% Retención ({factura.porcentaje_retencion}%)</span>
                    <span className="text-red-600">-{formatCurrency(factura.importe_retencion)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span>Total + IVA/IGIC - Retenciones:</span>
                  <span className="font-bold">{formatCurrency(factura.total - factura.total_suplidos)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total suplidos:</span>
                  <span>{formatCurrency(factura.total_suplidos)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(factura.total)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Total pagado:</span>
                  <span>{formatCurrency(factura.total_pagado)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-blue-600 border-t pt-2">
                  <span>Total a liquidar:</span>
                  <span>{formatCurrency(factura.total_pendiente)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pagos a cuenta */}
      {(factura.pagos || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Pagos a cuenta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factura</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factura.pagos.map((pago) => (
                  <TableRow key={pago.id}>
                    <TableCell>{factura.numero_factura}</TableCell>
                    <TableCell>{formatDateTime(pago.fecha)}</TableCell>
                    <TableCell>{pago.descripcion || pago.metodo_pago}</TableCell>
                    <TableCell className="text-right font-medium text-green-600">{formatCurrency(pago.importe)}</TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEliminarPago(pago.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog de pago */}
      <Dialog open={isPagoDialogOpen} onOpenChange={setIsPagoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Cobro</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegistrarPago} className="space-y-4">
            <div>
              <Label>Importe (€) *</Label>
              <Input
                type="number"
                step="0.01"
                max={factura.total_pendiente}
                value={pagoForm.importe}
                onChange={(e) => setPagoForm({ ...pagoForm, importe: e.target.value })}
                required
              />
              <p className="text-sm text-gray-500 mt-1">Pendiente: {formatCurrency(factura.total_pendiente)}</p>
            </div>
            <div>
              <Label>Método de pago</Label>
              <Select 
                value={pagoForm.metodo_pago} 
                onValueChange={(v) => setPagoForm({ ...pagoForm, metodo_pago: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRANSFERENCIA BANCARIA">Transferencia bancaria</SelectItem>
                  <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                  <SelectItem value="TARJETA">Tarjeta</SelectItem>
                  <SelectItem value="BIZUM">Bizum</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referencia</Label>
              <Input
                value={pagoForm.referencia}
                onChange={(e) => setPagoForm({ ...pagoForm, referencia: e.target.value })}
                placeholder="Nº transferencia, etc."
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={pagoForm.descripcion}
                onChange={(e) => setPagoForm({ ...pagoForm, descripcion: e.target.value })}
                placeholder="Notas del pago..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPagoDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Registrar Cobro
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de edición */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar {tipoInfo.label}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleUpdateFactura} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editForm.tipo} onValueChange={(v) => setEditForm({ ...editForm, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="proforma">Proforma</SelectItem>
                      <SelectItem value="factura">Factura</SelectItem>
                      <SelectItem value="presupuesto">Presupuesto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cliente</Label>
                  <Select value={editForm.cliente_id} onValueChange={(v) => setEditForm({ ...editForm, cliente_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {contactos.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Expediente (opcional)</Label>
                  <Select value={editForm.expediente_id || 'none'} onValueChange={(v) => setEditForm({ ...editForm, expediente_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Ninguno" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguno</SelectItem>
                      {expedientes.map(e => (
                        <SelectItem key={e.id} value={e.id.toString()}>{e.numero_expediente}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha emisión</Label>
                  <Input
                    type="date"
                    value={editForm.fecha_emision}
                    onChange={(e) => setEditForm({ ...editForm, fecha_emision: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fecha vencimiento</Label>
                  <Input
                    type="date"
                    value={editForm.fecha_vencimiento}
                    onChange={(e) => setEditForm({ ...editForm, fecha_vencimiento: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Descripción</Label>
                <Input
                  value={editForm.descripcion}
                  onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })}
                  placeholder="Ej: SERVICIOS JURIDICOS"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Forma de pago</Label>
                  <Select value={editForm.forma_pago} onValueChange={(v) => setEditForm({ ...editForm, forma_pago: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRANSFERENCIA BANCARIA">Transferencia bancaria</SelectItem>
                      <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                      <SelectItem value="TARJETA">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>% IVA</Label>
                  <Input
                    type="number"
                    value={editForm.porcentaje_iva}
                    onChange={(e) => setEditForm({ ...editForm, porcentaje_iva: e.target.value })}
                  />
                </div>
                <div>
                  <Label>% Retención IRPF</Label>
                  <Input
                    type="number"
                    value={editForm.porcentaje_retencion}
                    onChange={(e) => setEditForm({ ...editForm, porcentaje_retencion: e.target.value })}
                  />
                </div>
              </div>

              {/* Conceptos */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium">Conceptos facturables</h4>
                  <Button type="button" size="sm" onClick={addConcepto}>
                    <Plus className="w-4 h-4 mr-1" /> Añadir
                  </Button>
                </div>
                {editForm.conceptos.map((c, i) => (
                  <div key={i} className="grid grid-cols-6 gap-2 mb-2 items-end">
                    <Input
                      placeholder="Concepto"
                      value={c.concepto}
                      onChange={(e) => updateConcepto(i, 'concepto', e.target.value)}
                    />
                    <Input
                      placeholder="Descripción"
                      value={c.descripcion}
                      onChange={(e) => updateConcepto(i, 'descripcion', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Uds"
                      value={c.unidades}
                      onChange={(e) => updateConcepto(i, 'unidades', e.target.value)}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Precio"
                      value={c.precio}
                      onChange={(e) => updateConcepto(i, 'precio', e.target.value)}
                    />
                    <Input
                      type="number"
                      readOnly
                      value={(c.base_imponible || c.unidades * c.precio).toFixed(2)}
                      className="bg-gray-50"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeConcepto(i)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <Label>Observaciones</Label>
                <Textarea
                  value={editForm.notas}
                  onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })}
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Guardar Cambios
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacturaDetalle;
